import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs, { Dayjs } from 'dayjs';
import { useAppSelector } from '../../../modules/store/hooks';
import { marketingPersonApi, OtherMarketingPersonPayment } from '../../../modules/services/marketingPersonService';

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess: (payoutId: number) => void;
  marketingPersonId: number;
  marketingPersonName?: string;
}

type Step1 = 'period';
type Step2 = 'events';
type Step3 = 'referrals';
type Step4 = 'confirm';
type WizardStep = Step1 | Step2 | Step3 | Step4;

const STEPS: { key: WizardStep; label: string }[] = [
  { key: 'period',    label: 'Period' },
  { key: 'events',    label: 'Events' },
  { key: 'referrals', label: 'Referral commissions' },
  { key: 'confirm',   label: 'Confirm' },
];

const AddPayoutWizard: React.FC<Props> = ({ open, onClose, onSuccess, marketingPersonId, marketingPersonName }) => {
  const language = useAppSelector(state => state.auth.language);
  const langCode = language?.split('-')?.[0] || 'en';

  const [step, setStep] = useState<WizardStep>('period');
  const [dateFrom, setDateFrom] = useState<string>(dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState<string>(dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'));

  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());
  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<Set<number>>(new Set());
  const [note, setNote] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [otherPaidWarningOpen, setOtherPaidWarningOpen] = useState(false);

  useEffect(() => {
    if (!open) {
      setStep('period');
      setSelectedEventIds(new Set());
      setSelectedAssignmentIds(new Set());
      setNote('');
      setError('');
      setOtherPaidWarningOpen(false);
    }
  }, [open]);

  const shouldFetchEvents = open && (step === 'events' || step === 'referrals' || step === 'confirm');
  const shouldFetchReferrals = open && (step === 'referrals' || step === 'confirm');

  const {
    data: eventsData,
    isFetching: eventsLoading,
  } = marketingPersonApi.usePreviewPayoutEventsQuery(
    { marketingPersonId, dateFrom, dateTo },
    { skip: !shouldFetchEvents || !dateFrom || !dateTo },
  );

  const {
    data: referralsData,
    isFetching: referralsLoading,
  } = marketingPersonApi.usePreviewPayoutReferralsQuery(
    { marketingPersonId },
    { skip: !shouldFetchReferrals },
  );

  const {
    data: otherPayments,
    isFetching: otherPaymentsLoading,
  } = marketingPersonApi.usePreviewPayoutOtherPaymentsQuery(
    { marketingPersonId },
    { skip: !open || step !== 'confirm', refetchOnMountOrArgChange: true },
  );

  const [createPayout, { isLoading: creating }] = marketingPersonApi.useCreatePayoutMutation();

  // Auto-select all eligible events when list arrives
  useEffect(() => {
    if (!eventsData) return;
    setSelectedEventIds(prev => {
      if (prev.size > 0) return prev;
      const next = new Set<number>();
      for (const ev of eventsData.events) {
        if (!ev.alreadyPaid) next.add(ev.eventId);
      }
      return next;
    });
  }, [eventsData]);

  const eventItems = useMemo(() => eventsData?.events ?? [], [eventsData]);
  const referralOrganizers = useMemo(() => referralsData?.organizers ?? [], [referralsData]);

  const selectedEvents = useMemo(
    () => eventItems.filter(e => selectedEventIds.has(e.eventId) && !e.alreadyPaid),
    [eventItems, selectedEventIds],
  );
  const selectedReferrals = useMemo(
    () => referralOrganizers.filter(r => selectedAssignmentIds.has(r.assignmentId)),
    [referralOrganizers, selectedAssignmentIds],
  );

  // Organizers in this payout that another marketing person has already been paid for.
  const otherPaidConflicts = useMemo(() => {
    const eventOrgIds = new Set(selectedEvents.map(e => e.organizerId));
    const referralOrgIds = new Set(selectedReferrals.map(r => r.organizerId));
    return (otherPayments ?? [])
      .filter(p => eventOrgIds.has(p.organizerId) || referralOrgIds.has(p.organizerId))
      .map(p => ({
        ...p,
        sameEventsCount: p.eventIds.filter(id => selectedEventIds.has(id)).length,
        sameReferral: p.referralCommission > 0 && referralOrgIds.has(p.organizerId),
      }));
  }, [otherPayments, selectedEvents, selectedReferrals, selectedEventIds]);

  const selectedTotals = useMemo(() => {
    const eventCommission = selectedEvents.reduce((s, e) => s + Number(e.commission || 0), 0);
    const referralCommission = selectedReferrals.reduce((s, r) => s + Number(r.referralCommission || 0), 0);
    const tickets = selectedEvents.reduce((s, e) => s + Number(e.ticketsSold || 0), 0);
    const sales = selectedEvents.reduce((s, e) => s + Number(e.totalSales || 0), 0);
    const fees = selectedEvents.reduce((s, e) => s + Number(e.totalFees || 0), 0);
    return {
      eventCommission,
      referralCommission,
      total: eventCommission + referralCommission,
      tickets,
      sales,
      fees,
    };
  }, [selectedEvents, selectedReferrals]);

  const toggleEvent = (id: number) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllEvents = () => {
    const eligible = eventItems.filter(e => !e.alreadyPaid).map(e => e.eventId);
    setSelectedEventIds(prev => {
      const allSelected = eligible.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(eligible);
    });
  };

  const toggleReferral = (aId: number) => {
    setSelectedAssignmentIds(prev => {
      const next = new Set(prev);
      if (next.has(aId)) next.delete(aId); else next.add(aId);
      return next;
    });
  };

  const toggleAllReferrals = () => {
    const all = referralOrganizers.map(r => r.assignmentId);
    setSelectedAssignmentIds(prev => {
      const allSelected = all.every(id => prev.has(id));
      return allSelected ? new Set() : new Set(all);
    });
  };

  const goNext = () => {
    setError('');
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx < STEPS.length - 1) setStep(STEPS[idx + 1].key);
  };
  const goBack = () => {
    setError('');
    const idx = STEPS.findIndex(s => s.key === step);
    if (idx > 0) setStep(STEPS[idx - 1].key);
  };

  const canProceedFromPeriod = Boolean(dateFrom && dateTo && dayjs(dateFrom).isValid() && dayjs(dateTo).isValid() && dayjs(dateFrom).isBefore(dayjs(dateTo).add(1, 'day')));

  const handleSave = () => {
    setError('');
    if (selectedEvents.length === 0 && selectedReferrals.length === 0) {
      setError('Select at least one event or referral commission');
      return;
    }
    if (otherPaidConflicts.length > 0) {
      setOtherPaidWarningOpen(true);
      return;
    }
    submitPayout();
  };

  const submitPayout = async () => {
    setOtherPaidWarningOpen(false);
    try {
      const result = await createPayout({
        marketingPersonId,
        body: {
          dateFrom,
          dateTo,
          eventItems: selectedEvents.map(e => ({ eventId: e.eventId })),
          referralItems: selectedReferrals.map(r => ({ assignmentId: r.assignmentId })),
          note: note || undefined,
        },
        language: langCode,
      }).unwrap();
      onSuccess(result.id);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Failed to create payout');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle color="warning.main" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>
          Add payout
          {marketingPersonName ? ` — ${marketingPersonName}` : ''}
        </span>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stepper activeStep={STEPS.findIndex(s => s.key === step)} sx={{ mb: 3 }}>
          {STEPS.map(s => (
            <Step key={s.key}>
              <StepLabel>{s.label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {step === 'period' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              <Typography variant="body2" color="textSecondary">
                Choose the date range (uses event <em>end date</em>) for the payout.
              </Typography>
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <DatePicker
                  label="Date from"
                  value={dateFrom ? dayjs(dateFrom) : null}
                  onChange={(v: Dayjs | null) => setDateFrom(v ? v.format('YYYY-MM-DD') : '')}
                  renderInput={(params) => <TextField {...params} size="small" color="warning" />}
                />
                <DatePicker
                  label="Date to"
                  value={dateTo ? dayjs(dateTo) : null}
                  onChange={(v: Dayjs | null) => setDateTo(v ? v.format('YYYY-MM-DD') : '')}
                  renderInput={(params) => <TextField {...params} size="small" color="warning" />}
                />
              </div>
            </div>
          )}

          {step === 'events' && (
            <div>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Events ended in {dateFrom} — {dateTo} across organizers assigned to this marketer.
                Rows greyed out are already included in a previous confirmed payout.
              </Typography>
              {eventsLoading ? <CircularProgress color="warning" /> : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          color="warning"
                          checked={eventItems.filter(e => !e.alreadyPaid).length > 0 && eventItems.filter(e => !e.alreadyPaid).every(e => selectedEventIds.has(e.eventId))}
                          onChange={toggleAllEvents}
                        />
                      </TableCell>
                      <TableCell>Event ID</TableCell>
                      <TableCell>Event Name</TableCell>
                      <TableCell>Organizer</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Tickets</TableCell>
                      <TableCell align="right">Sales</TableCell>
                      <TableCell align="right">Fees</TableCell>
                      <TableCell align="right">Commission</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">No eligible events.</TableCell>
                      </TableRow>
                    )}
                    {eventItems.map(ev => (
                      <TableRow key={ev.eventId} sx={{ opacity: ev.alreadyPaid ? 0.4 : 1 }}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            color="warning"
                            disabled={ev.alreadyPaid}
                            checked={selectedEventIds.has(ev.eventId)}
                            onChange={() => toggleEvent(ev.eventId)}
                          />
                        </TableCell>
                        <TableCell>{ev.eventId}</TableCell>
                        <TableCell>
                          {ev.eventName}
                          {ev.alreadyPaid && (
                            <Tooltip title={`Already in payout #${ev.paidInPayoutId}`}>
                              <Chip label="Paid" size="small" color="success" sx={{ ml: 1 }} />
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell>{ev.organizerName || ev.organizerPhone || `#${ev.organizerId}`}</TableCell>
                        <TableCell>{ev.eventEndDate ? new Date(ev.eventEndDate).toLocaleDateString() : '-'}</TableCell>
                        <TableCell align="right">{ev.ticketsSold}</TableCell>
                        <TableCell align="right">₪{Number(ev.totalSales || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">₪{Number(ev.totalFees || 0).toLocaleString()}</TableCell>
                        <TableCell align="right"><strong>₪{Number(ev.commission || 0).toLocaleString()}</strong></TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={4}><strong>TOTAL (selected)</strong></TableCell>
                      <TableCell align="right"><strong>{selectedTotals.tickets}</strong></TableCell>
                      <TableCell align="right"><strong>₪{selectedTotals.sales.toLocaleString()}</strong></TableCell>
                      <TableCell align="right"><strong>₪{selectedTotals.fees.toLocaleString()}</strong></TableCell>
                      <TableCell align="right"><strong>₪{selectedTotals.eventCommission.toLocaleString()}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {step === 'referrals' && (
            <div>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                Organizers assigned to this marketer whose one-time referral commission is not yet paid.
              </Typography>
              {referralsLoading ? <CircularProgress color="warning" /> : (
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          color="warning"
                          checked={referralOrganizers.length > 0 && referralOrganizers.every(r => selectedAssignmentIds.has(r.assignmentId))}
                          onChange={toggleAllReferrals}
                        />
                      </TableCell>
                      <TableCell>Organizer ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell align="right">Sales (all time)</TableCell>
                      <TableCell align="right">Fees (all time)</TableCell>
                      <TableCell align="right">Sales (60d)</TableCell>
                      <TableCell align="right">Fees (60d)</TableCell>
                      <TableCell align="right">Referral</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {referralOrganizers.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={9} align="center">No eligible referral commissions.</TableCell>
                      </TableRow>
                    )}
                    {referralOrganizers.map(r => (
                      <TableRow key={r.assignmentId}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            color="warning"
                            checked={selectedAssignmentIds.has(r.assignmentId)}
                            onChange={() => toggleReferral(r.assignmentId)}
                          />
                        </TableCell>
                        <TableCell>{r.organizerId}</TableCell>
                        <TableCell>{r.organizerName}</TableCell>
                        <TableCell>{r.organizerPhone}</TableCell>
                        <TableCell align="right">₪{Number(r.totalSalesAllTime || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">₪{Number(r.totalFeesAllTime || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">₪{Number(r.totalSales60Days || 0).toLocaleString()}</TableCell>
                        <TableCell align="right">₪{Number(r.totalFees60Days || 0).toLocaleString()}</TableCell>
                        <TableCell align="right"><strong>₪{Number(r.referralCommission || 0).toLocaleString()}</strong></TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell />
                      <TableCell colSpan={7}><strong>TOTAL (selected)</strong></TableCell>
                      <TableCell align="right"><strong>₪{selectedTotals.referralCommission.toLocaleString()}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </div>
          )}

          {step === 'confirm' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <Typography variant="body2" color="textSecondary">
                Review the payout, add an optional note, then confirm.
              </Typography>
              <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                <SummaryStat label="Period"                value={`${dateFrom} → ${dateTo}`} />
                <SummaryStat label="Events selected"       value={String(selectedEvents.length)} />
                <SummaryStat label="Referrals selected"    value={String(selectedReferrals.length)} />
                <SummaryStat label="Tickets"               value={String(selectedTotals.tickets)} />
                <SummaryStat label="Sales"                 value={`₪${selectedTotals.sales.toLocaleString()}`} />
                <SummaryStat label="Handling fees"         value={`₪${selectedTotals.fees.toLocaleString()}`} />
                <SummaryStat label="Event commission"      value={`₪${selectedTotals.eventCommission.toLocaleString()}`} />
                <SummaryStat label="Referral commission"   value={`₪${selectedTotals.referralCommission.toLocaleString()}`} />
                <SummaryStat label="TOTAL PAYOUT"          value={`₪${selectedTotals.total.toLocaleString()}`} strong />
              </div>
              <TextField
                label="Note (optional)"
                size="small"
                color="warning"
                value={note}
                onChange={e => setNote(e.target.value)}
                multiline
                minRows={2}
              />
            </div>
          )}
        </LocalizationProvider>

        {error && <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="warning">Cancel</Button>
        {step !== 'period' && (
          <Button onClick={goBack} color="warning">Back</Button>
        )}
        {step !== 'confirm' && (
          <Button
            onClick={goNext}
            color="warning"
            variant="contained"
            disabled={step === 'period' && !canProceedFromPeriod}
          >
            {step === 'events' ? 'Add referral commissions' : step === 'referrals' ? 'Continue' : 'Next'}
          </Button>
        )}
        {step === 'confirm' && (
          <Button onClick={handleSave} color="warning" variant="contained" disabled={creating || otherPaymentsLoading}>
            {creating ? 'Saving…' : 'Save payout'}
          </Button>
        )}
      </DialogActions>

      <OtherPaidWarningDialog
        open={otherPaidWarningOpen}
        conflicts={otherPaidConflicts}
        onCancel={() => setOtherPaidWarningOpen(false)}
        onConfirm={submitPayout}
        confirming={creating}
      />
    </Dialog>
  );
};

type OtherPaidConflict = OtherMarketingPersonPayment & { sameEventsCount: number; sameReferral: boolean };

const OtherPaidWarningDialog: React.FC<{
  open: boolean;
  conflicts: OtherPaidConflict[];
  onCancel: () => void;
  onConfirm: () => void;
  confirming: boolean;
}> = ({ open, conflicts, onCancel, onConfirm, confirming }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth>
    <DialogTitle color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <WarningAmberIcon color="warning" />
      Organizer already paid to another marketing person
    </DialogTitle>
    <DialogContent dividers>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Another marketing person has already received money for the organizers below.
        Do you still want to pay this marketing person for them?
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Organizer</TableCell>
            <TableCell>Paid to</TableCell>
            <TableCell>Payout</TableCell>
            <TableCell>Date</TableCell>
            <TableCell align="right">Event commission</TableCell>
            <TableCell align="right">Referral</TableCell>
            <TableCell>Overlap with this payout</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {conflicts.map(c => (
            <TableRow key={`${c.organizerId}-${c.marketingPersonId}-${c.payoutId ?? 'legacy'}`}>
              <TableCell>{c.organizerName || `#${c.organizerId}`}</TableCell>
              <TableCell>
                {c.marketingPersonName || c.marketingPersonPhone}
                {c.marketingPersonName && c.marketingPersonPhone && (
                  <Typography variant="caption" display="block" color="textSecondary">{c.marketingPersonPhone}</Typography>
                )}
              </TableCell>
              <TableCell>{c.payoutId ? `#${c.payoutId}` : 'Manual (legacy)'}</TableCell>
              <TableCell>{c.paidAt ? new Date(c.paidAt).toLocaleDateString() : '-'}</TableCell>
              <TableCell align="right">₪{Number(c.eventCommission || 0).toLocaleString()}</TableCell>
              <TableCell align="right">₪{Number(c.referralCommission || 0).toLocaleString()}</TableCell>
              <TableCell>
                {c.sameEventsCount > 0 && (
                  <Chip size="small" color="error" label={`${c.sameEventsCount} same event${c.sameEventsCount > 1 ? 's' : ''}`} sx={{ mr: 0.5, mb: 0.5 }} />
                )}
                {c.sameReferral && <Chip size="small" color="error" label="Referral" sx={{ mb: 0.5 }} />}
                {c.sameEventsCount === 0 && !c.sameReferral && (
                  <Typography variant="caption" color="textSecondary">Other events only</Typography>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel} color="warning">Cancel</Button>
      <Button onClick={onConfirm} color="error" variant="contained" disabled={confirming}>
        {confirming ? 'Saving…' : 'Confirm payout'}
      </Button>
    </DialogActions>
  </Dialog>
);

const SummaryStat: React.FC<{ label: string; value: string; strong?: boolean }> = ({ label, value, strong }) => (
  <div style={{ padding: 12, borderRadius: 8, background: '#ede9e4', minWidth: 160 }}>
    <Typography variant="body2" color="textSecondary">{label}</Typography>
    <Typography variant={strong ? 'h6' : 'body1'} color={strong ? 'success.main' : 'warning.main'} fontWeight={strong ? 'bold' : 500}>
      {value}
    </Typography>
  </div>
);

export default AddPayoutWizard;
